/* eslint-disable react/forbid-prop-types */
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Container, Row, Col } from 'react-bootstrap';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  FiCheckCircle,
  FiArrowLeft,
  FiPrinter,
  FiSmartphone,
  FiKey,
  FiClock,
  FiGlobe,
  FiUser,
  FiMail,
  FiPhone,
  FiCreditCard,
  FiInfo,
} from 'react-icons/fi';
import { getEsimDetailsApi } from '../../api/esimApi';
import { ESIM_ROUTES } from '../../constants/esimConstants';
import { formatPrice } from '../../utils/esimUtils';
import './EsimVoucherPage.scss';

function StatusPill({ status }) {
  const s = (status || '').toLowerCase();
  let label = (status || 'Pending').toUpperCase();
  let modifier = 'pending';
  if (s === 'confirmed' || s === 'success') {
    label = 'CONFIRMED';
    modifier = 'success';
  } else if (s.includes('fail')) {
    modifier = 'error';
  }
  return (
    <span className={`esim-voucher__pill esim-voucher__pill--${modifier}`}>
      <FiCheckCircle />
      {label}
    </span>
  );
}

StatusPill.defaultProps = { status: '' };
StatusPill.propTypes = { status: PropTypes.string };

function Field({ icon, label, value, mono }) {
  return (
    <div className="esim-voucher__field">
      <span className="esim-voucher__field-label">
        {icon}
        {label}
      </span>
      <span
        className={`esim-voucher__field-value ${
          mono ? 'esim-voucher__field-value--mono' : ''
        }`}
      >
        {value || '—'}
      </span>
    </div>
  );
}

Field.propTypes = {
  icon: PropTypes.node,
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
  mono: PropTypes.bool,
};

Field.defaultProps = { icon: null, value: '', mono: false };

function EsimVoucherPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const navState = useMemo(() => location.state || {}, [location.state]);
  const { transactionId } = useParams();
  const txId = decodeURIComponent(transactionId || '');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!txId) {
      setLoading(false);
      setError('Missing transaction id in the URL.');
      return () => {};
    }
    setLoading(true);
    setError('');
    (async () => {
      const result = await getEsimDetailsApi(txId);
      if (cancelled) return;
      if (!result) {
        setError(
          'We could not fetch the eSIM transaction. Please contact support.',
        );
      } else {
        setData(result);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [txId]);

  // Fall back to nav state if API call fails / for instant render.
  const fallbackPlan = navState?.plan || null;
  const fallbackTraveler = navState?.traveler || null;
  const fallbackSearch = navState?.search || null;

  const view = useMemo(() => {
    if (data) {
      return {
        txid: data.txid,
        txdate: data.txdate,
        bookingstatus: data.bookingstatus,
        currency: data.currency || 'INR',
        baseFare: Number(data.basefare || 0),
        tax: Number(data.tax || 0),
        handling: Number(data.handlingcharges || 0),
        total: Number(data.totalinvoice || data.totalfarequote || 0),
        plan: {
          planId: data.planid,
          planName: data.planname,
          genericProductName: data.genericProductName,
          genericVariantName: data.genericVariantName,
          validity: data.validity,
        },
        traveler: {
          title: data.paxtitle,
          firstName: data.paxfirstname,
          lastName: data.paxlastname,
          dateOfBirth: data.paxdob,
          email: data.paxemail,
          mobile: data.paxmobile,
          passportNumber: data.paxpassportnumber,
          nationality: data.paxnationality,
        },
        trip: {
          country: data.paxcountry || fallbackSearch?.country || '',
          start: data.trveldatestart,
          end: data.traveldateend,
        },
        sim: {
          orderId: data.orderid_api,
          activationCode: data.activation_code_api,
          apns: data.apns_api,
          iccid: data.id_api || data.sim_no_api,
          lpa: data.lpa_api,
          mobileNumber: data.mobile_no_api,
          qrCode: data.base64QRCode_api,
          installStatus: data.esiminstallationstatus,
        },
      };
    }
    if (!fallbackPlan) return null;
    const total = Number(
      fallbackPlan?.totalPrice || fallbackPlan?.netPrice || 0,
    );
    const baseFare = Number(
      fallbackPlan?.baseFare || fallbackPlan?.netPrice || 0,
    );
    const tax = Number(fallbackPlan?.tax || 0);
    const handling = Number(fallbackPlan?.handlingCharges || 0);
    return {
      txid: navState.transactionId || txId,
      txdate: '',
      bookingstatus: 'pending',
      currency: fallbackPlan?.currency || 'INR',
      baseFare,
      tax,
      handling,
      total,
      plan: {
        planId: fallbackPlan?.planId,
        planName: fallbackPlan?.planName,
        genericProductName: fallbackPlan?.genericProductName,
        genericVariantName: fallbackPlan?.genericVariantName,
        validity: fallbackPlan?.validity,
      },
      traveler: fallbackTraveler
        ? {
            title: fallbackTraveler.title,
            firstName: fallbackTraveler.firstName,
            lastName: fallbackTraveler.lastName,
            dateOfBirth: fallbackTraveler.dateOfBirth,
            email: fallbackTraveler.email,
            mobile: fallbackTraveler.mobile,
            passportNumber: fallbackTraveler.passportNumber,
            nationality: fallbackTraveler.nationality,
          }
        : {},
      trip: {
        country: fallbackSearch?.country || '',
        start: fallbackSearch?.travelStart || '',
        end: fallbackSearch?.travelEnd || '',
      },
      sim: {},
    };
  }, [data, fallbackPlan, fallbackTraveler, fallbackSearch, navState, txId]);

  const handlePrint = () => {
    if (typeof window !== 'undefined' && window.print) window.print();
  };

  // const handleDownload = () => {
  //   if (typeof window !== 'undefined' && window.print) window.print();
  // };

  if (loading) {
    return (
      <div className="esim-voucher esim-voucher--loading">
        <Container>
          <div className="esim-voucher__loading-card">
            Loading your eSIM voucher…
          </div>
        </Container>
      </div>
    );
  }

  if (error && !view) {
    return (
      <div className="esim-voucher esim-voucher--error">
        <Container>
          <div className="esim-voucher__error-card">
            <h2>We hit a snag</h2>
            <p>{error}</p>
            <button
              type="button"
              className="esim-voucher__back"
              onClick={() => navigate(ESIM_ROUTES.LANDING)}
            >
              <FiArrowLeft /> Back to eSIM Home
            </button>
          </div>
        </Container>
      </div>
    );
  }

  const fullName = [
    view?.traveler?.title,
    view?.traveler?.firstName,
    view?.traveler?.lastName,
  ]
    .filter(Boolean)
    .join(' ');

  let qrSrc = '';
  if (view?.sim?.qrCode) {
    qrSrc = view.sim.qrCode.startsWith('data:')
      ? view.sim.qrCode
      : `data:image/png;base64,${view.sim.qrCode}`;
  }

  return (
    <main className="esim-voucher">
      <section className="esim-voucher__hero">
        <Container>
          <div className="esim-voucher__hero-inner">
            <div>
              <span className="esim-voucher__hero-eyebrow">
                eSIM Booking Confirmation
              </span>
              <h1 className="esim-voucher__hero-title">
                <FiCheckCircle /> {navState?.message || 'Your eSIM is booked!'}
              </h1>
              <p className="esim-voucher__hero-sub">
                We&apos;ve received your booking. Use the activation details
                below to install the eSIM on your compatible device.
              </p>
              <div className="esim-voucher__hero-meta">
                <Field
                  icon={<FiKey />}
                  label="Transaction ID"
                  value={view?.txid}
                  mono
                />
                {view?.txdate && (
                  <Field
                    icon={<FiClock />}
                    label="Booked On"
                    value={view.txdate}
                  />
                )}
                <StatusPill status={view?.bookingstatus} />
              </div>
            </div>
            <div className="esim-voucher__hero-actions">
              <button
                type="button"
                className="esim-voucher__hero-btn esim-voucher__hero-btn--ghost"
                onClick={handlePrint}
              >
                <FiPrinter />
                Print
              </button>
            </div>
          </div>
        </Container>
      </section>

      <Container className="esim-voucher__body">
        <Row className="g-4">
          <Col lg={8} md={12}>
            <section className="esim-voucher__card">
              <header className="esim-voucher__card-head">
                <h3>
                  <FiSmartphone /> eSIM Plan
                </h3>
              </header>
              <div className="esim-voucher__grid">
                <Field
                  icon={<FiSmartphone />}
                  label="Plan"
                  value={view?.plan?.genericProductName || view?.plan?.planName}
                />
                <Field label="Variant" value={view?.plan?.genericVariantName} />
                <Field
                  label="Validity"
                  value={
                    view?.plan?.validity
                      ? `${view.plan.validity} ${
                          Number(view.plan.validity) === 1 ? 'Day' : 'Days'
                        }`
                      : ''
                  }
                />
                <Field
                  icon={<FiGlobe />}
                  label="Destination"
                  value={view?.trip?.country}
                />
                <Field label="Travel Start" value={view?.trip?.start} />
                <Field label="Travel End" value={view?.trip?.end} />
              </div>
            </section>

            <section className="esim-voucher__card">
              <header className="esim-voucher__card-head">
                <h3>
                  <FiUser /> Traveller Details
                </h3>
              </header>
              <div className="esim-voucher__grid">
                <Field icon={<FiUser />} label="Name" value={fullName} />
                <Field
                  label="Date of Birth"
                  value={view?.traveler?.dateOfBirth}
                />
                <Field
                  icon={<FiCreditCard />}
                  label="Passport"
                  value={view?.traveler?.passportNumber}
                  mono
                />
                <Field
                  label="Nationality"
                  value={view?.traveler?.nationality}
                />
                <Field
                  icon={<FiMail />}
                  label="Email"
                  value={view?.traveler?.email}
                />
                <Field
                  icon={<FiPhone />}
                  label="Mobile"
                  value={view?.traveler?.mobile}
                />
              </div>
            </section>

            <section className="esim-voucher__card">
              <header className="esim-voucher__card-head">
                <h3>
                  <FiKey /> Activation Details
                </h3>
              </header>
              {qrSrc || view?.sim?.activationCode || view?.sim?.iccid ? (
                <div className="esim-voucher__activation">
                  {qrSrc && (
                    <div className="esim-voucher__qr">
                      <img src={qrSrc} alt="eSIM QR Code" />
                      <span>Scan with your phone&apos;s Camera app</span>
                    </div>
                  )}
                  <div className="esim-voucher__activation-fields">
                    <Field
                      icon={<FiKey />}
                      label="Activation Code"
                      value={view?.sim?.activationCode}
                      mono
                    />
                    <Field label="LPA" value={view?.sim?.lpa} mono />
                    <Field
                      label="ICCID / SIM No."
                      value={view?.sim?.iccid}
                      mono
                    />
                    <Field label="APN" value={view?.sim?.apns} mono />
                    <Field
                      icon={<FiPhone />}
                      label="Mobile Number"
                      value={view?.sim?.mobileNumber}
                      mono
                    />
                    <Field label="Order ID" value={view?.sim?.orderId} mono />
                  </div>
                </div>
              ) : (
                <div className="esim-voucher__activation-empty">
                  <FiInfo />
                  <div>
                    <strong>Activation details will arrive shortly.</strong>
                    <p>
                      Once the eSIM is provisioned (typically within a few
                      minutes), the QR code and activation details will be
                      emailed to{' '}
                      <strong>{view?.traveler?.email || 'your inbox'}</strong>{' '}
                      and visible here on refresh.
                    </p>
                  </div>
                </div>
              )}
              {view?.sim?.installStatus && (
                <p className="esim-voucher__install-status">
                  Installation status: <strong>{view.sim.installStatus}</strong>
                </p>
              )}
            </section>
          </Col>

          <Col lg={4} md={12}>
            <section className="esim-voucher__fare-card">
              <header className="esim-voucher__card-head">
                <h3>Payment Summary</h3>
              </header>
              <ul className="esim-voucher__fare-rows">
                <li>
                  <span>Base fare</span>
                  <span>
                    {formatPrice(view?.baseFare, view?.currency) ||
                      `${view?.currency} ${view?.baseFare}`}
                  </span>
                </li>
                <li>
                  <span>Taxes &amp; Fees</span>
                  <span>
                    {formatPrice(view?.tax, view?.currency) ||
                      `${view?.currency} ${view?.tax}`}
                  </span>
                </li>
                {view?.handling > 0 && (
                  <li>
                    <span>Handling charges</span>
                    <span>
                      {formatPrice(view?.handling, view?.currency) ||
                        `${view?.currency} ${view?.handling}`}
                    </span>
                  </li>
                )}
              </ul>
              <div className="esim-voucher__fare-divider" />
              <div className="esim-voucher__fare-total">
                <span>Total Paid</span>
                <strong>
                  {formatPrice(view?.total, view?.currency) ||
                    `${view?.currency} ${view?.total}`}
                </strong>
              </div>
            </section>

            <button
              type="button"
              className="esim-voucher__home-btn"
              onClick={() => navigate(ESIM_ROUTES.LANDING)}
            >
              <FiArrowLeft />
              Book another eSIM
            </button>
          </Col>
        </Row>
      </Container>
    </main>
  );
}

export default EsimVoucherPage;
